import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import toast from "react-hot-toast";
import { FiAlertTriangle, FiArrowLeft, FiCheck, FiX, FiCopy, FiFlag, FiMoreVertical, FiUserPlus } from "react-icons/fi";
import Dialog from "../../components/UI/Dialog";
import ConfirmDialog from "../../components/UI/ConfirmDialog";
import { StateBlock } from "../../components/UI";
import MapThumb from "../../components/UI/MapThumb";
import { Page, Action, TextArea, Select, FormField, request, usePoll } from "./shared";
import { useAuthStore } from "../../store/authStore";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDismiss } from "../../hooks/useDismiss";
import { menuKeys } from "../../hooks/menuKeys";
import { displayMap } from "../../lib/format";
import { mapImage } from "../../lib/mapImages";
import { monogram } from "./home/primitives";
import { burstAt } from "../../lib/fx";
import { playBan, playMatchFound, playReady, playStep, playTick, playYourTurn } from "../../lib/sfx";

// Backend пока сообщает причину отмены русской строкой.
const ROSTER_CHANGED = "Состав изменился";
const PLAYER_REASONS = ["cheating", "abuse", "griefing", "other"];
const SERVER_REASONS = ["lag", "connection", "other"];

/* ───────────────────────── эффекты этапов ───────────────────────── */
const slam = keyframes`
  0% { opacity: 0; transform: scale(2.4); letter-spacing: 0.5em; filter: blur(12px); }
  22% { opacity: 1; transform: scale(0.96); letter-spacing: 0.04em; filter: blur(0); }
  32% { transform: scale(1.03); }
  42% { transform: scale(1); }
  82% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.08); }
`;
const streak = keyframes`
  0% { transform: scaleX(0); opacity: 1; }
  35% { transform: scaleX(1); opacity: 1; }
  100% { transform: scaleX(1); opacity: 0; }
`;
const backdrop = keyframes`
  0% { opacity: 0; }
  12% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
`;
const tickPop = keyframes`
  0% { transform: scale(1.25); }
  100% { transform: scale(1); }
`;
const banShake = keyframes`
  0% { transform: translateX(0) scale(1); }
  12% { transform: translateX(-8px) scale(1.02); }
  24% { transform: translateX(8px) scale(1.02); }
  36% { transform: translateX(-5px); }
  48% { transform: translateX(5px); }
  60% { transform: translateX(0) scale(1); opacity: 1; }
  100% { transform: scale(0.9); opacity: 0.45; }
`;
const crossIn = keyframes`
  from { transform: scale(0) rotate(-30deg); opacity: 0; }
  to { transform: scale(1) rotate(0); opacity: 1; }
`;
const readyPop = keyframes`
  0% { transform: scale(0.97); box-shadow: 0 0 0 0 rgba(119, 214, 168, 0.7); }
  40% { transform: scale(1.015); box-shadow: 0 0 0 18px rgba(119, 214, 168, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(119, 214, 168, 0); }
`;
const FoundOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2400;
  pointer-events: none;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 14px;
  background: radial-gradient(70% 60% at 50% 50%, rgba(11, 14, 18, 0.92), rgba(11, 14, 18, 0.55) 70%, transparent);
  animation: ${backdrop} 2.2s ease both;
  h2 {
    font-size: clamp(40px, 9vw, 104px);
    line-height: 1;
    font-weight: 900;
    text-transform: uppercase;
    white-space: nowrap;
    background: linear-gradient(100deg, #ff7a2f, #ffd2a8 45%, #fff1e0 55%, #ff9b57);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 0 28px rgba(255, 155, 87, 0.55));
    animation: ${slam} 2.2s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  p {
    font-size: 18px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textSecondary};
    animation: ${slam} 2.2s 0.08s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .line {
    width: min(70vw, 900px);
    height: 3px;
    background: linear-gradient(90deg, transparent, #ff9b57, #fff1e0, #ff9b57, transparent);
    box-shadow: 0 0 22px #ff9b57;
    animation: ${streak} 1.4s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
`;

/* ───────────────────────── шапка матча ───────────────────────── */
const Head = styled.header`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px 24px;
  padding: 8px 0;
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
  h1 {
    font-size: 28px;
    line-height: 36px;
    font-weight: 700;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
    @media (max-width: 767px) {
      font-size: 24px;
      line-height: 32px;
    }
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-top: 4px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .stage {
    font-weight: 600;
    color: ${(p) => p.theme.colors.text};
  }
  .side {
    justify-self: end;
    text-align: right;
    @media (max-width: 767px) {
      justify-self: start;
      text-align: left;
    }
  }
`;
const Timer = styled.div<{ $urgent?: boolean }>`
  display: grid;
  gap: 2px;
  .label {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .t {
    font-size: 40px;
    line-height: 44px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    display: inline-block;
    transform-origin: right center;
    color: ${(p) => (p.$urgent ? p.theme.colors.danger : "inherit")};
    text-shadow: ${(p) => (p.$urgent ? "0 0 24px rgba(255, 138, 145, 0.6)" : "none")};
    animation: ${(p) => (p.$urgent ? tickPop : "none")} 0.5s ease-out;
  }
`;
const Result = styled.div<{ $kind: string }>`
  display: grid;
  gap: 4px;
  .kind {
    font-size: 15px;
    font-weight: 650;
    color: ${(p) => (p.$kind === "win" ? p.theme.colors.success : p.$kind === "loss" ? p.theme.colors.danger : p.theme.colors.textSecondary)};
  }
  .score {
    font-size: 48px;
    line-height: 52px;
    font-weight: 700;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
    @media (max-width: 767px) {
      font-size: 40px;
      line-height: 44px;
    }
  }
  .teams {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Surface = styled.section`
  min-width: 0;
  display: grid;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
  .hint {
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  &.live {
    box-shadow: inset 0 0 0 1px rgba(119, 214, 168, 0.5), 0 0 36px rgba(119, 214, 168, 0.1);
  }
  &.fx {
    animation: ${readyPop} 0.9s ease-out 2;
  }
`;
const Banner = styled.div<{ $tone: "warn" | "bad" | "info" }>`
  display: grid;
  gap: 4px;
  padding: 16px 20px;
  border-radius: 12px;
  background: ${(p) => (p.$tone === "bad" ? p.theme.colors.dangerSoft : p.$tone === "warn" ? p.theme.colors.warningSoft : p.theme.colors.infoSoft)};
  strong {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    color: ${(p) => (p.$tone === "bad" ? p.theme.colors.danger : p.$tone === "warn" ? p.theme.colors.warning : p.theme.colors.info)};
  }
  span {
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;

/* ───────────────────────── карты ───────────────────────── */
const Maps = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
`;
const MapCard = styled.li<{ $selected: boolean; $banned?: boolean }>`
  position: relative;
  animation: ${(p) => (p.$banned ? banShake : "none")} 1.1s ease-out forwards;
  transition: transform 0.18s cubic-bezier(0.2, 1.4, 0.4, 1);
  &:hover:not(:has(button:disabled)) {
    transform: translateY(-3px);
  }
  .x {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: grid;
    place-items: center;
    background: rgba(11, 14, 18, 0.7);
    color: ${(p) => p.theme.colors.danger};
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    animation: ${crossIn} 0.35s cubic-bezier(0.2, 1.6, 0.4, 1) both;
    svg { width: 56px; height: 56px; stroke-width: 3; }
  }
  ${(p) => (p.$banned ? "img { filter: grayscale(1); }" : "")}
  display: grid;
  gap: 8px;
  padding: 8px 8px 12px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.raised};
  box-shadow: ${(p) => (p.$selected ? `0 0 0 2px ${p.theme.colors.primary}` : "none")};
  .pic {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: 8px;
    background: ${(p) => p.theme.colors.navigation};
    display: grid;
    place-items: center;
    color: ${(p) => p.theme.colors.textMuted};
    img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    &::after {
      content: "";
      position: absolute;
      inset: auto 0 0 0;
      height: 55%;
      background: linear-gradient(to top, #0b0e12cc, transparent);
    }
    .name {
      position: absolute;
      left: 10px;
      bottom: 8px;
      z-index: 1;
      font-size: 16px;
      font-weight: 700;
      color: #fff;
    }
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 4px;
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .mine {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: ${(p) => p.theme.colors.primary};
    font-weight: 650;
  }
  button {
    min-height: 44px;
    margin: 0 4px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 650;
    background: ${(p) => (p.$selected ? p.theme.colors.primary : p.theme.colors.surfaceHover)};
    color: ${(p) => (p.$selected ? p.theme.colors.onPrimary : p.theme.colors.text)};
    transition: background ${(p) => p.theme.motion.fast};
    &:hover:not(:disabled) {
      background: ${(p) => (p.$selected ? p.theme.colors.primaryHover : "#2d3b4b")};
    }
    &:disabled {
      cursor: not-allowed;
      color: ${(p) => p.theme.colors.textMuted};
      background: ${(p) => p.theme.colors.input};
    }
  }
`;
const Quiet = styled.button`
  justify-self: start;
  min-height: 44px;
  padding: 0 12px;
  margin-left: -12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  &:hover:not(:disabled) {
    background: ${(p) => p.theme.colors.dangerSoft};
    color: ${(p) => p.theme.colors.danger};
  }
  &:disabled {
    opacity: 0.5;
  }
`;

/* ───────────────────────── подготовка сервера ───────────────────────── */
const sweep = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(350%); }
`;
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245, 158, 91, 0.55); }
  70% { box-shadow: 0 0 0 10px rgba(245, 158, 91, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 91, 0); }
`;
const zoom = keyframes`
  from { transform: scale(1.04) translate(0, 0); }
  to { transform: scale(1.16) translate(-2%, -1.5%); }
`;
const scan = keyframes`
  from { transform: translateX(-60%) skewX(-18deg); }
  to { transform: translateX(260%) skewX(-18deg); }
`;
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;
const nodePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 155, 87, 0.7); }
  100% { box-shadow: 0 0 0 16px rgba(255, 155, 87, 0); }
`;
const Prep = styled.section`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 24px;
  min-height: 380px;
  padding: 28px 32px 28px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.navigation};
  box-shadow: inset 0 0 0 1px rgba(255, 155, 87, 0.28);
  @media (max-width: 767px) {
    min-height: 340px;
    padding: 20px 16px;
  }
  .bg {
    position: absolute;
    inset: 0;
    z-index: -3;
    background-size: cover;
    background-position: center;
    animation: ${zoom} 22s ease-in-out infinite alternate;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
  .shade {
    position: absolute;
    inset: 0;
    z-index: -2;
    background:
      linear-gradient(90deg, rgba(11, 14, 18, 0.92) 0%, rgba(11, 14, 18, 0.55) 55%, rgba(11, 14, 18, 0.25) 100%),
      linear-gradient(0deg, rgba(11, 14, 18, 0.95) 0%, transparent 55%);
  }
  .glint {
    position: absolute;
    inset: -10% auto -10% 0;
    width: 28%;
    z-index: -1;
    background: linear-gradient(90deg, transparent, rgba(255, 155, 87, 0.22), transparent);
    animation: ${scan} 4.5s ease-in-out infinite;
    @media (prefers-reduced-motion: reduce) {
      display: none;
    }
  }
  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(11, 14, 18, 0.6);
    backdrop-filter: blur(6px);
    box-shadow: inset 0 0 0 1px rgba(255, 155, 87, 0.45);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.primary};
    i {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${(p) => p.theme.colors.primary};
      box-shadow: 0 0 12px ${(p) => p.theme.colors.primary};
      animation: ${blink} 1.2s ease-in-out infinite;
    }
  }
  .elapsed {
    font-size: 36px;
    line-height: 40px;
    font-weight: 800;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 2px 20px rgba(0, 0, 0, 0.6);
  }
  .body {
    align-self: end;
    display: grid;
    gap: 6px;
  }
  .kicker {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  h2 {
    font-size: clamp(44px, 8vw, 88px);
    line-height: 0.95;
    font-weight: 900;
    letter-spacing: -0.03em;
    text-transform: uppercase;
    text-shadow: 0 4px 40px rgba(0, 0, 0, 0.7);
  }
  .now {
    font-size: 17px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.text};
  }
  .rail {
    position: relative;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    padding-top: 18px;
    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 6px;
      left: 12.5%;
      height: 4px;
      border-radius: 2px;
    }
    &::before {
      right: 12.5%;
      background: rgba(255, 255, 255, 0.14);
    }
    &::after {
      width: calc(var(--p) * 75%);
      background: linear-gradient(90deg, #ff7a2f, #ffd2a8);
      box-shadow: 0 0 16px rgba(255, 155, 87, 0.8);
      transition: width 0.9s cubic-bezier(0.2, 0.9, 0.2, 1);
    }
  }
  .step {
    position: relative;
    display: grid;
    justify-items: center;
    gap: 8px;
    text-align: center;
    font-size: 13px;
    line-height: 17px;
    color: ${(p) => p.theme.colors.textMuted};
    transition: color 0.4s;
    .node {
      position: absolute;
      top: -18px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${(p) => p.theme.colors.navigation};
      box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.22);
      transition: all 0.4s;
    }
    &.done {
      color: ${(p) => p.theme.colors.textSecondary};
      .node {
        background: ${(p) => p.theme.colors.primary};
        box-shadow: 0 0 14px rgba(255, 155, 87, 0.8);
      }
    }
    &.active {
      color: ${(p) => p.theme.colors.text};
      font-weight: 700;
      .node {
        background: #fff1e0;
        box-shadow: 0 0 0 3px ${(p) => p.theme.colors.primary};
        animation: ${nodePulse} 1.4s ease-out infinite;
      }
    }
    span.txt {
      padding-top: 10px;
    }
    @media (max-width: 767px) {
      font-size: 11px;
      line-height: 14px;
    }
  }
`;
const PREP_STEPS = ["reserve", "boot", "map", "plugin"];

function ServerPrep({ phase, map, t }: { phase: string; map: string; t: (k: string, v?: any) => string }) {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSec((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, []);
  /* Шаги идут от момента перехода в running, а не от открытия страницы: так не бывает двух «блипов» подряд. */
  const runFrom = useRef<number | null>(null);
  if (phase !== "starting" && runFrom.current === null) runFrom.current = sec;
  const active = phase === "starting" ? 0 : Math.min(3, 1 + Math.floor((sec - (runFrom.current ?? sec)) / 7));
  const lastActive = useRef(active);
  useEffect(() => {
    if (active > lastActive.current) playStep();
    lastActive.current = active;
  }, [active]);
  const img = mapImage(map);
  const name = map ? displayMap(map) : "";
  return (
    <Prep aria-labelledby="connect-title" role="status" style={{ ["--p" as string]: active / 3 }}>
      {img && <div className="bg" style={{ backgroundImage: `url(${img})` }} aria-hidden="true" />}
      <div className="shade" aria-hidden="true" />
      <div className="glint" aria-hidden="true" />
      <div className="top">
        <span className="chip">
          <i aria-hidden="true" />
          {t("match.prep.title")}
        </span>
        <span className="elapsed" aria-label={t("match.prep.elapsed")}>
          {mmss(sec)}
        </span>
      </div>
      <div className="body">
        <span className="kicker">{t("match.prep.kicker")}</span>
        <h2 id="connect-title">{name || t("match.map_unknown")}</h2>
        <p className="now">{t(`match.prep.${PREP_STEPS[active]}`, { map: name })}…</p>
      </div>
      <div className="rail">
        {PREP_STEPS.map((k, i) => (
          <div key={k} className={`step${i < active ? " done" : i === active ? " active" : ""}`}>
            <span className="node" aria-hidden="true" />
            <span className="txt">{t(`match.prep.short.${k}`)}</span>
          </div>
        ))}
      </div>
    </Prep>
  );
}

/* ───────────────────────── подключение ───────────────────────── */
const Connect = styled.div`
  display: grid;
  gap: 12px;
  dl {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px 24px;
  }
  dt {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  dd {
    font-size: 16px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
  code {
    display: block;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${(p) => p.theme.colors.navigation};
    font-size: 14px;
    overflow-wrap: anywhere;
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    a {
      box-shadow: 0 6px 24px rgba(255, 155, 87, 0.35);
    }
  }
`;

/* ───────────────────────── составы ───────────────────────── */
const Teams = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;
const Roster = styled.ul<{ $stats: boolean }>`
  list-style: none;
  display: grid;
  gap: 2px;
  margin: 0 -8px;
  li {
    position: relative;
    display: grid;
    grid-template-columns: ${(p) => (p.$stats ? "auto minmax(0, 1fr) auto auto auto" : "auto minmax(0, 1fr) auto")};
    align-items: center;
    gap: 12px;
    min-height: 56px;
    padding: 6px 4px 6px 8px;
    border-radius: 10px;
    &.me {
      background: ${(p) => p.theme.colors.raised};
    }
  }
  .av {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => p.theme.colors.textSecondary};
    font-size: 13px;
    font-weight: 650;
  }
  .nick {
    min-width: 0;
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    overflow-wrap: anywhere;
    a:hover {
      text-decoration: underline;
    }
    em {
      margin-left: 8px;
      padding: 0 6px;
      border-radius: 6px;
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.textSecondary};
      font-size: 11px;
      font-style: normal;
    }
  }
  &&& .nick a {
    color: ${(p) => p.theme.colors.text};
  }
  .num {
    min-width: 8ch;
    text-align: right;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .mvp {
    min-width: 3ch;
    text-align: right;
    font-size: 15px;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 520px) {
    li {
      grid-template-columns: ${(p) => (p.$stats ? "auto minmax(0, 1fr) auto auto" : "auto minmax(0, 1fr) auto")};
    }
    .mvp {
      display: none;
    }
  }
`;
const Kebab = styled.button`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  color: ${(p) => p.theme.colors.textSecondary};
  &:hover,
  &[aria-expanded="true"] {
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => p.theme.colors.text};
  }
`;
const Menu = styled.div`
  position: absolute;
  right: 4px;
  top: calc(100% - 4px);
  z-index: 5;
  min-width: 220px;
  padding: 6px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.surfaceHover};
  border: 1px solid ${(p) => p.theme.colors.border};
  box-shadow: 0 16px 40px #0009;
  button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.text};
    text-align: left;
    &:hover,
    &:focus-visible {
      background: ${(p) => p.theme.colors.input};
    }
    svg {
      width: 16px;
      height: 16px;
      color: ${(p) => p.theme.colors.textSecondary};
    }
  }
`;

function PlayerMenu({
  name,
  canFriend,
  canReport,
  onFriend,
  onReport,
}: {
  name: string;
  canFriend: boolean;
  canReport: boolean;
  onFriend: () => void;
  onReport: () => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, box, btn);
  if (!canFriend && !canReport) return <span style={{ width: 44 }} />;
  return (
    <div ref={box} style={{ position: "relative" }}>
      <Kebab ref={btn} type="button" aria-haspopup="menu" aria-expanded={open} aria-label={t("match.player_actions", { name })} title={t("match.player_actions", { name })} onClick={() => setOpen((v) => !v)}>
        <FiMoreVertical aria-hidden="true" />
      </Kebab>
      {open && (
        <Menu role="menu" aria-label={name} onKeyDown={menuKeys} ref={(el) => el?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()}>
          {canFriend && (
            <button
              type="button"
              role="menuitem"
              aria-label={t("home.add_friend_named", { name })}
              onClick={() => {
                close();
                onFriend();
              }}
            >
              <FiUserPlus aria-hidden="true" />
              {t("home.add_friend")}
            </button>
          )}
          {canReport && (
            <button
              type="button"
              role="menuitem"
              aria-label={t("match.report_player_named", { name })}
              onClick={() => {
                close();
                onReport();
              }}
            >
              <FiFlag aria-hidden="true" />
              {t("match.report_player")}
            </button>
          )}
        </Menu>
      )}
    </div>
  );
}

/** Названия команд вида «Команда X» формирует сервер на русском: локализуем только шаблон, не ник. */
const teamLabel = (t: (k: string, p?: Record<string, string | number>) => string, name: string) => {
  const m = /^Команда (.+)$/.exec(name || "");
  return m ? t("match.team_named", { name: m[1] }) : name;
};
const mmss = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;

export default function GameMatch() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const { t } = useLanguage();
  const user = useAuthStore((s) => s.user);
  const [finished, setFinished] = useState(false);
  const {
    data: m,
    error,
    errorStatus,
    reload,
  } = usePoll<any>(matchId ? "/play/matches/" + matchId : null, 3000, finished);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState<{ steam?: string; name?: string; server?: boolean } | null>(null);
  const [category, setCategory] = useState("other");
  const [reason, setReason] = useState("");
  const [leaving, setLeaving] = useState(false);
  const phase = m?.phase as string | undefined;
  const done = !!phase && ["finished", "cancelled", "error"].includes(phase);
  useEffect(() => {
    if (done) setFinished(true);
  }, [done]);
  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [done]);
  const previousPhase = useRef("");
  useEffect(() => {
    if (previousPhase.current === "voting" && m?.phase === "cancelled" && m?.error === ROSTER_CHANGED) nav("/");
    previousPhase.current = m?.phase || "";
  }, [m?.phase, m?.error, nav]);

  const teams: any[] = m?.config?.teams || [];
  const ownTeam = teams.findIndex((tm: any) => (tm.players || []).some((p: any) => p.steam_id === user?.steamId));
  const results: any[] = m?.result?.teams || [];

  /* Карты: убранная карта не исчезает рывком, а «выбивается» и гаснет. */
  const [display, setDisplay] = useState<string[]>([]);
  const [banned, setBanned] = useState<string[]>([]);
  const shownRef = useRef<string[] | null>(null);
  const ownBan = useRef(0);
  const mapsKey = ((m?.maps as string[]) || []).join(",");
  const loaded = !!m;
  useEffect(() => {
    if (!loaded) return;
    const cur = mapsKey ? mapsKey.split(",") : [];
    const prev = shownRef.current;
    shownRef.current = cur;
    if (prev === null) {
      setDisplay(cur);
      return;
    }
    const removed = prev.filter((x) => !cur.includes(x));
    if (!removed.length) {
      setDisplay(cur);
      return;
    }
    if (Date.now() - ownBan.current > 1500) playBan();
    ownBan.current = 0;
    setBanned(removed);
    setDisplay(prev);
    const id = window.setTimeout(() => {
      setDisplay(cur);
      setBanned([]);
    }, 1100);
    return () => window.clearTimeout(id);
  }, [mapsKey, loaded]);

  /* «Матч найден»: один раз на матч, при первом показе вето. */
  const [found, setFound] = useState(false);
  const isMember = !!m?.member;
  useEffect(() => {
    if (phase !== "voting" || !isMember || !matchId) return;
    const key = "cshub.found." + matchId;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      return;
    }
    setFound(true);
    playMatchFound();
    window.setTimeout(() => burstAt(window.innerWidth / 2, window.innerHeight / 2), 450);
    window.setTimeout(() => setFound(false), 2300);
  }, [phase, isMember, matchId]);

  /* Ваш ход и тики последних секунд. */
  const turn: number = m?.turn ?? 0;
  const myTurnNow = phase === "voting" && ownTeam >= 0 && ownTeam === turn % 2;
  const prevMine = useRef<boolean | null>(null);
  useEffect(() => {
    if (phase !== "voting") {
      prevMine.current = null;
      return;
    }
    if (prevMine.current === false && myTurnNow) playYourTurn();
    prevMine.current = myTurnNow;
  }, [phase, myTurnNow]);
  const left = phase === "voting" ? Math.max(0, Math.ceil((Date.parse(m?.deadline) - now) / 1000)) : 0;
  useEffect(() => {
    if (myTurnNow && left > 0 && left <= 5) playTick();
  }, [left, myTurnNow]);

  /* Сервер готов: фанфары и вспышка, если это произошло на глазах. */
  const serverReady = phase === "running" && !!m?.pluginReady && !!m?.ip;
  const prevReady = useRef<boolean | null>(null);
  const [readyFx, setReadyFx] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    if (prevReady.current === false && serverReady) {
      playReady();
      window.setTimeout(() => burstAt(window.innerWidth / 2, 360), 280);
      setReadyFx(true);
      window.setTimeout(() => setReadyFx(false), 2000);
    }
    prevReady.current = serverReady;
  }, [serverReady, loaded]);

  async function vote(map: string) {
    try {
      setBusy(true);
      /* Отклик сразу по клику, не дожидаясь сервера: звук и «выбитая» карта. */
      ownBan.current = Date.now();
      playBan();
      setBanned([map]);
      await request("/play/matches/" + matchId + "/vote", { map, turn: m.turn });
      reload();
    } catch (e) {
      ownBan.current = 0;
      setBanned([]);
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function leaveAndCancel() {
    setBusy(true);
    try {
      await request("/play/party", { action: "leave" });
      nav("/");
    } catch (e) {
      toast.error((e as Error).message);
      setBusy(false);
      setLeaving(false);
    }
  }
  async function sendReport() {
    if (sending) return;
    setSending(true);
    try {
      const label = t(`match.reason.${report?.server ? "server" : "player"}.${category}`);
      await request("/reports", {
        matchId,
        steamId: report?.steam,
        kind: report?.server ? "server" : "player",
        body: `[${label}] ${reason.trim()}`,
      });
      toast.success(t("match.report_sent"));
      setReport(null);
      setReason("");
    } catch (e) {
      // Текст жалобы сохраняется для повторной отправки.
      toast.error((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  const back = (
    <Link to="/stats" style={{ justifySelf: "start", display: "inline-flex", alignItems: "center", gap: 8, minHeight: 44, fontSize: 14 }}>
      <FiArrowLeft aria-hidden="true" /> {t("match.back_history")}
    </Link>
  );

  if (!m)
    return (
      <Page>
        {back}
        {error ? (
          <StateBlock
            kind="error"
            title={errorStatus === 404 ? t("match.not_found") : t("match.load_error")}
            text={errorStatus === 404 ? t("match.not_found_text") : error}
            actionLabel={errorStatus === 404 ? t("match.to_home") : t("match.retry")}
            onAction={errorStatus === 404 ? () => nav("/") : reload}
          />
        ) : (
          <StateBlock kind="loading" title={t("match.loading")} rows={3} />
        )}
      </Page>
    );

  const command = `connect ${m.ip}:${m.port}; password ${m.password || ""}`;
  const mapKnown = !!m.config?.map;
  const mapName = mapKnown ? displayMap(m.config.map) : "";
  const phaseKey = `match.phase.${phase}`;
  const phaseLabel = phase && t(phaseKey) !== phaseKey ? t(phaseKey) : "";
  const stageText = phase === "running" && !m.pluginReady ? t("match.phase.starting") : phaseLabel;
  const ownScore = results.length && ownTeam >= 0 ? results[ownTeam]?.score : undefined;
  const otherScore = results.length && ownTeam >= 0 ? results[1 - ownTeam]?.score : undefined;
  const outcome = ownScore === undefined || otherScore === undefined ? "" : ownScore > otherScore ? "win" : ownScore < otherScore ? "loss" : "draw";
  const hasScore = phase === "finished" && results.length > 0;
  const hasStats = !!m.result?.players?.length;
  const secondsLeft = phase === "voting" ? Math.max(0, Math.ceil((Date.parse(m.deadline) - now) / 1000)) : 0;
  const turnTeam = teams[m.turn % 2];
  const myTurn = ownTeam >= 0 && ownTeam === m.turn % 2;
  const reasons = report?.server ? SERVER_REASONS : PLAYER_REASONS;
  const failed = phase === "error";
  const title = phase === "voting" || !mapKnown ? (phase === "voting" ? t("match.title_veto") : mapName || stageText || t("match.map_unknown")) : mapName;

  return (
    <Page>
      {found && (
        <FoundOverlay aria-hidden="true">
          <div className="line" />
          <h2>{t("match.found")}</h2>
          <p>{t("match.title_veto")}</p>
          <div className="line" />
        </FoundOverlay>
      )}
      {back}
      <Head>
        {mapKnown && mapImage(m.config.map) ? <MapThumb map={m.config.map} width={144} height={81} /> : <span />}
        <div>
          <h1>{title}</h1>
          <div className="meta">
            {stageText && <span className="stage">{stageText}</span>}
            <span>5×5 · {t("match.region")}</span>
            {phase === "voting" && teams.length > 0 && <span>{t("match.turn_of", { team: teamLabel(t, turnTeam?.name || "") })}</span>}
          </div>
        </div>
        <div className="side">
          {phase === "voting" && (
            <Timer $urgent={left > 0 && left <= 5}>
              <span className="label">{t("match.time_left")}</span>
              <span className="t" key={left <= 5 ? left : "calm"} role="timer" aria-live="off">
                {mmss(secondsLeft)}
              </span>
            </Timer>
          )}
          {hasScore && (
            <Result $kind={outcome}>
              <span className="kind">{outcome ? t(`home.result.${outcome}`) : t("match.finished")}</span>
              <span className="score" aria-label={t("match.score")}>
                {outcome ? `${ownScore} : ${otherScore}` : results.map((tm: any) => tm.score).join(" : ")}
              </span>
              {!outcome && teams.length === 2 && (
                <span className="teams">
                  {teamLabel(t, teams[0].name)} · {teamLabel(t, teams[1].name)}
                </span>
              )}
            </Result>
          )}
          {phase === "cancelled" && <span className="stage">{t("match.cancelled")}</span>}
        </div>
      </Head>

      {error && (
        <Banner $tone="warn" role="status">
          <strong>
            <FiAlertTriangle aria-hidden="true" />
            {t("match.sync_lost")}
          </strong>
        </Banner>
      )}
      {phase === "cancelled" && m.error && (
        <Banner $tone="warn">
          <strong>{t("match.cancelled")}</strong>
          <span>{t("match.reason_label", { text: m.error })}</span>
        </Banner>
      )}
      {failed && (
        <Banner $tone="bad" role="alert">
          <strong>
            <FiAlertTriangle aria-hidden="true" />
            {t("match.start_failed")}
          </strong>
          <span>{t("match.start_failed_text")}</span>
        </Banner>
      )}

      {phase === "voting" && teams.length > 0 && (
        <Surface aria-labelledby="veto-title">
          <div>
            <h2 id="veto-title">{myTurn ? t("match.turn_of", { team: teamLabel(t, turnTeam?.name || "") }) : t("match.wait_turn")}</h2>
            <p className="hint">{t("match.vote_hint")}</p>
          </div>
          <Maps>
            {(display.length ? display : m.maps || []).map((map: string) => {
              const isBanned = banned.includes(map);
              const mine = m.votes?.[user?.steamId || ""] === map;
              const votes = Object.values(m.votes || {}).filter((v) => v === map).length;
              const img = mapImage(map);
              return (
                <MapCard key={map} $selected={mine} $banned={isBanned}>
                  <div className="pic">
                    {isBanned && (
                      <span className="x" aria-hidden="true">
                        <FiX />
                      </span>
                    )}
                    {img ? <img src={img} alt="" loading="lazy" /> : <span aria-hidden="true">{monogram(displayMap(map))}</span>}
                    <span className="name">{displayMap(map)}</span>
                  </div>
                  <div className="row">
                    <span>{t("match.votes_n", { n: votes })}</span>
                    {mine && (
                      <span className="mine">
                        <FiCheck aria-hidden="true" />
                        {t("match.your_vote")}
                      </span>
                    )}
                  </div>
                  <button type="button" disabled={busy || !myTurn || isBanned} onClick={() => vote(map)} aria-label={`${t("match.ban")}: ${displayMap(map)}`}>
                    {myTurn ? t("match.ban") : t("match.wait_turn")}
                  </button>
                </MapCard>
              );
            })}
          </Maps>
          {m.member && (
            <Quiet type="button" disabled={busy} onClick={() => setLeaving(true)}>
              {t("match.leave_cancel")}
            </Quiet>
          )}
        </Surface>
      )}

      {(phase === "starting" || (phase === "running" && !m.pluginReady)) && m.member && (
        <ServerPrep phase={phase} map={m.config?.map || ""} t={t} />
      )}

      {phase === "running" && m.member && m.pluginReady && (
        <Surface className={readyFx ? "live fx" : "live"} aria-labelledby="connect-title">
          <h2 id="connect-title">{t("match.connect_title")}</h2>
          {m.ip ? (
            <Connect>
              <dl>
                <div>
                  <dt>IP</dt>
                  <dd>
                    {m.ip}:{m.port}
                  </dd>
                </div>
                <div>
                  <dt>{t("match.password")}</dt>
                  <dd>{m.password || t("match.no_password")}</dd>
                </div>
              </dl>
              <code>{command}</code>
              <div className="acts">
                <Action as="a" $variant="primary" href={`steam://connect/${m.ip}:${m.port}/${encodeURIComponent(m.password || "")}`}>
                  {t("match.connect")}
                </Action>
                <Action
                  type="button"
                  onClick={() =>
                    navigator.clipboard
                      .writeText(command)
                      .then(() => toast.success(t("match.copied")))
                      .catch(() => toast.error(t("match.copy_failed")))
                  }
                >
                  <FiCopy aria-hidden="true" />
                  {t("match.copy")}
                </Action>
              </div>
              <p className="hint">{t("match.connect_hint")}</p>
            </Connect>
          ) : (
            <p className="hint" role="status">
              {t("match.preparing")}
            </p>
          )}
        </Surface>
      )}

      {teams.length > 0 && (
        <Teams>
          {teams.map((tm: any, ti: number) => (
            <Surface key={tm.id} aria-labelledby={"team-" + ti}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <h2 id={"team-" + ti}>
                  {teamLabel(t, tm.name)}
                  {ti === ownTeam && <small style={{ marginLeft: 8, fontWeight: 500, opacity: 0.7 }}>· {t("match.your_team")}</small>}
                </h2>
                <span className="hint" title={hasStats ? t("match.stats_hint") : undefined}>
                  {hasStats ? t("match.stats_head") : t("match.members_n", { n: (tm.players || []).length })}
                </span>
              </div>
              <Roster $stats={hasStats}>
                {(tm.players || []).map((p: any) => {
                  const stats = m.result?.players?.find((v: any) => v.steam_id === p.steam_id)?.stats;
                  const id = m.playerIds?.[p.steam_id];
                  const mine = p.steam_id === user?.steamId;
                  return (
                    <li key={p.steam_id} className={mine ? "me" : undefined}>
                      <span className="av" aria-hidden="true">
                        {monogram(p.name)}
                      </span>
                      <span className="nick">
                        {id ? <Link to={"/profile/" + id}>{p.name}</Link> : p.name}
                        {mine && <em>{t("home.you")}</em>}
                      </span>
                      {hasStats && (
                        <>
                          <span className="num">{stats ? `${stats.kills} / ${stats.deaths} / ${stats.assists}` : "—"}</span>
                          <span className="mvp" title="MVP">
                            {stats?.mvps ?? ""}
                          </span>
                        </>
                      )}
                      <PlayerMenu
                        name={p.name}
                        canFriend={!!id && id !== user?.id}
                        canReport={!!m.member && !!id && id !== user?.id}
                        onFriend={() =>
                          request("/friends/requests", { userId: id })
                            .then(() => toast.success(t("match.request_sent")))
                            .catch((e) => toast.error(e.message))
                        }
                        onReport={() => {
                          setCategory("other");
                          setReport({ steam: p.steam_id, name: p.name });
                        }}
                      />
                    </li>
                  );
                })}
              </Roster>
            </Surface>
          ))}
        </Teams>
      )}

      {(m.member || done) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {done && m.member && !m.legacy && (
            <Action
              $variant="primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  if (m.error !== ROSTER_CHANGED) await request("/play/party", { action: "new" });
                  nav("/");
                } catch (e) {
                  toast.error((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("match.new")}
            </Action>
          )}
          {m.member && phase !== "voting" && (
            <Action
              $variant="ghost"
              onClick={() => {
                setCategory("other");
                setReport({ server: true });
              }}
            >
              <FiFlag aria-hidden="true" />
              {t("match.report_server")}
            </Action>
          )}
        </div>
      )}

      {report && (
        <Dialog
          size="md"
          label={t("match.report")}
          onClose={() => setReport(null)}
          dirty={reason.trim().length > 0}
          footer={
            <>
              <Action onClick={() => setReport(null)} disabled={sending}>
                {t("common.cancel")}
              </Action>
              <Action $variant="primary" disabled={reason.trim().length < 3 || sending} aria-busy={sending} onClick={sendReport}>
                {sending ? t("match.sending") : t("match.send")}
              </Action>
            </>
          }
        >
          <h2>{report.server ? t("match.report_server") : t("match.report_player")}</h2>
          <p style={{ fontSize: 14, opacity: 0.8 }}>
            {report.server ? `${mapName || t("match.map_unknown")} · ${matchId}` : `${report.name} · ${mapName || matchId}`}
          </p>
          <FormField>
            {t("match.reason")}
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {reasons.map((r) => (
                <option key={r} value={r}>
                  {t(`match.reason.${report.server ? "server" : "player"}.${r}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField>
            {t("match.report_text")}
            <TextArea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={3000} placeholder={t("match.report_placeholder")} />
          </FormField>
        </Dialog>
      )}
      {leaving && (
        <ConfirmDialog
          title={t("match.leave_title")}
          description={t("match.leave_text")}
          confirmLabel={t("match.leave_confirm")}
          busy={busy}
          onConfirm={leaveAndCancel}
          onClose={() => setLeaving(false)}
        />
      )}
    </Page>
  );
}
